import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardActionArea,
  Typography,
  Grid,
  Container,
  IconButton,
  Box,
  Divider,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import { 
  Home as HomeIcon, 
  ChevronRight, 
  Settings as SettingsIcon, 
  Add as AddIcon, 
  Delete as DeleteIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import * as Icons from '@mui/icons-material';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { addSetting, deleteSetting, syncSettings, updateSetting } from '../services/settingsService';

// Array di configurazione per gli elementi del menu con descrizioni
const menuItems = [
  {
    label: 'Catalogo Prodotti',
    description: 'Visualizza, modifica e aggiungi nuovi prodotti al catalogo',
    icon: 'Storefront',
    path: '/techyshop/products',
    color: '#ff9800',
  },
  {
    label: 'Attributi Prodotti',
    description: 'Gestisci le caratteristiche e varianti dei prodotti',
    icon: 'ListAlt',
    path: '/techyshop/attributes',
    color: '#03a9f4',
  },
  {
    label: 'Categorie Prodotto',
    description: 'Organizza i prodotti in categorie e sottocategorie',
    icon: 'Category',
    path: '/techyshop/categories',
    color: '#e91e63',
  },
  {
    label: 'Gestione Traduzioni',
    description: 'Configura le lingue e i testi per lo shop online',
    icon: 'Translate',
    path: '/techyshop/translations',
    color: '#3f51b5',
  },
  {
    label: 'Gestione Utenti',
    description: 'Controlla gli accessi e i profili dei clienti techyshop',
    icon: 'Group',
    path: '/techyshop/users',
    color: '#4caf50',
  },
  {
    label: 'Gestione Banners',
    description: 'Personalizza i banner promozionali e le offerte dello shop',
    icon: 'ViewCarousel',
    path: '/techyshop/banners',
    color: '#9c27b0',
  },
  {
    label: 'Gestione Menu',
    description: 'Configura le voci del menu di navigazione dello shop',
    icon: 'Menu',
    path: '/techyshop/menu',
    color: '#f44336',
  },
  {
    label: 'Gestione Media',
    description: 'Gestisci file multimediali, immagini e gallerie dello shop',
    icon: 'PermMedia',
    path: '/techyshop/media',
    color: '#00bcd4',
  },
  {
    label: 'Vai allo Shop',
    description: 'Apri l\'interfaccia pubblica in una nuova scheda',
    icon: 'OpenInNew',
    action: () => window.open('https://techyshop.techysumo.it', '_blank'),
    color: '#009688',
  }
];

function Techyshop() {
  const navigate = useNavigate();
  const [isSettingsModalOpen, setIsSettingsModalOpen] = React.useState(false);
  const [newSetting, setNewSetting] = React.useState({ chiave: '', valore: '' });
  const [isSyncing, setIsSyncing] = React.useState(false);

  // Stato per la modifica in linea
  const [editingId, setEditingId] = React.useState(null);
  const [editForm, setEditForm] = React.useState({ chiave: '', valore: '' });

  // Query in tempo reale delle impostazioni
  const impostazioni = useLiveQuery(() => db.techyshop_impostazioni.toArray(), []);

  // Sincronizzazione automatica all'apertura del modal
  React.useEffect(() => {
    if (isSettingsModalOpen) {
      const performSync = async () => {
        setIsSyncing(true);
        await syncSettings();
        setIsSyncing(false);
      };
      performSync();
    }
  }, [isSettingsModalOpen]);

  const handleAction = (item) => {
    if (item.action) {
      item.action();
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await syncSettings();
    } catch (error) {
      console.error("Errore durante la sincronizzazione:", error);
      alert("Errore durante la sincronizzazione: " + error.message);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleAddSetting = async () => {
    if (!newSetting.chiave.trim()) return;
    try {
      const result = await addSetting(newSetting.chiave, newSetting.valore);
      if (result.success) {
        setNewSetting({ chiave: '', valore: '' });
      } else {
        alert("Errore nell'aggiunta: " + result.message);
      }
    } catch (error) {
      console.error("Errore nell'aggiunta dell'impostazione:", error);
    }
  };

  const handleDeleteSetting = async (id) => {
    if (window.confirm("Sei sicuro di voler eliminare questa impostazione?")) {
      try {
        const result = await deleteSetting(id);
        if (!result.success) {
          alert("Errore nella cancellazione: " + result.message);
        }
      } catch (error) {
        console.error("Errore nella cancellazione dell'impostazione:", error);
      }
    }
  };

  const startEditing = (row) => {
    setEditingId(row.id);
    setEditForm({ chiave: row.chiave, valore: row.valore });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({ chiave: '', valore: '' });
  };

  const handleUpdateSetting = async (id) => {
    try {
      const result = await updateSetting(id, editForm);
      if (result.success) {
        setEditingId(null);
      } else {
        alert("Errore nell'aggiornamento: " + result.message);
      }
    } catch (error) {
      console.error("Errore nell'aggiornamento dell'impostazione:", error);
    }
  };

  const handleKeyDownAdd = (e) => {
    if (e.key === 'Enter') {
      handleAddSetting();
    }
  };

  const handleKeyDownEdit = (e, id) => {
    if (e.key === 'Enter') {
      handleUpdateSetting(id);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Breadcrumbs & Navigation */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Breadcrumbs
          separator={<ChevronRight fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ color: 'rgba(255,255,255,0.7)' }}
        >
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'inherit' }}
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="primary.main" sx={{ fontWeight: 600 }}>TechyShop</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton 
            color="primary" 
            onClick={() => setIsSettingsModalOpen(true)} 
            sx={{ border: '1px solid', borderColor: 'primary.main' }}
          >
            <SettingsIcon />
          </IconButton>
          <IconButton 
            color="primary" 
            onClick={() => navigate('/')} 
            sx={{ border: '1px solid', borderColor: 'primary.main' }}
          >
            <HomeIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Header Section */}
      <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800 }}>
        Techy<Box component="span" sx={{ color: 'primary.main' }}>Shop</Box>
      </Typography>

      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, px: 1 }}>
        Strumenti di Gestione
      </Typography>

      <Grid container spacing={3}>
        {menuItems.map((item, index) => {
          const IconComponent = Icons[item.icon] || Icons.HelpOutline;
          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderLeft: `4px solid ${item.color}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    boxShadow: `0 8px 20px rgba(0,0,0,0.3), 0 0 10px ${item.color}33`,
                  }
                }}
              >
                <CardActionArea
                  onClick={() => handleAction(item)}
                  sx={{ height: '100%', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                >
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 3,
                    backgroundColor: `${item.color}22`,
                    color: item.color,
                    mb: 2
                  }}>
                    <IconComponent sx={{ fontSize: 32 }} />
                  </Box>

                  <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                    {item.label}
                  </Typography>

                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
                    {item.description}
                  </Typography>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Modal Impostazioni */}
      <Dialog 
        open={isSettingsModalOpen} 
        onClose={() => setIsSettingsModalOpen(false)}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: '#121212',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: 4,
          py: 3,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Impostazioni <Box component="span" sx={{ color: 'primary.main' }}>TechyShop</Box></Typography>
          <IconButton onClick={() => setIsSettingsModalOpen(false)} color="inherit" sx={{ border: '1px solid rgba(255,255,255,0.1)' }}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </DialogTitle>
        <DialogContent 
          sx={{ 
            px: 4, 
            pt: 0, 
            pb: 6,
            '&::-webkit-scrollbar': {
              width: '10px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '10px',
              border: '2px solid transparent',
              backgroundClip: 'content-box',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            },
          }}
        >
          <Box sx={{ mt: 6 }}>
            {/* Form Aggiunta */}
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Aggiungi Nuova Impostazione</Typography>
            <Box sx={{ mb: 6, display: 'flex', gap: 2, backgroundColor: 'rgba(255,255,255,0.02)', p: 3, borderRadius: 2, border: '1px solid rgba(255,255,255,0.05)' }}>
              <TextField
                label="Chiave"
                fullWidth
                placeholder="es: api_endpoint, timeout, ecc."
                value={newSetting.chiave}
                onChange={(e) => setNewSetting({ ...newSetting, chiave: e.target.value })}
                onKeyDown={handleKeyDownAdd}
              />
              <TextField
                label="Valore"
                fullWidth
                placeholder="Valore dell'impostazione"
                value={newSetting.valore}
                onChange={(e) => setNewSetting({ ...newSetting, valore: e.target.value })}
                onKeyDown={handleKeyDownAdd}
              />
              <Button 
                variant="contained" 
                size="large"
                startIcon={<AddIcon />} 
                onClick={handleAddSetting}
                sx={{ minWidth: 150 }}
              >
                Aggiungi
              </Button>
            </Box>

            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Elenco Impostazioni Salvate</Typography>
            <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold', width: '80px' }}>ID</TableCell>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Chiave</TableCell>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Valore</TableCell>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 'bold', width: '150px' }} align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {impostazioni?.map((row) => (
                    <TableRow key={row.id} hover sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' } }}>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.4)' }}>{row.id}</TableCell>
                      
                      {editingId === row.id ? (
                        <>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              value={editForm.chiave}
                              onChange={(e) => setEditForm({ ...editForm, chiave: e.target.value })}
                              onKeyDown={(e) => handleKeyDownEdit(e, row.id)}
                              autoFocus
                              sx={{ '& .MuiInputBase-input': { fontWeight: 700 } }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              value={editForm.valore}
                              onChange={(e) => setEditForm({ ...editForm, valore: e.target.value })}
                              onKeyDown={(e) => handleKeyDownEdit(e, row.id)}
                              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Salva (INVIO)">
                              <IconButton size="small" color="success" onClick={() => handleUpdateSetting(row.id)}>
                                <CheckIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Annulla (ESC)">
                              <IconButton size="small" color="inherit" onClick={cancelEditing}>
                                <ClearIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell sx={{ fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }} onClick={() => startEditing(row)}>
                            {row.chiave}
                          </TableCell>
                          <TableCell sx={{ color: 'rgba(255,255,255,0.8)', fontFamily: 'monospace', cursor: 'pointer' }} onClick={() => startEditing(row)}>
                            {row.valore}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Modifica">
                              <IconButton size="small" color="primary" onClick={() => startEditing(row)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Elimina">
                              <IconButton size="small" color="error" onClick={() => handleDeleteSetting(row.id)}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  {(!impostazioni || impostazioni.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 8, color: 'rgba(255,255,255,0.2)' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <SettingsIcon sx={{ fontSize: 48, opacity: 0.2 }} />
                          <Typography>Nessuna impostazione trovata in Dexie</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 4, borderTop: '1px solid rgba(255, 255, 255, 0.1)', justifyContent: 'space-between' }}>
          <Button 
            onClick={handleSync} 
            disabled={isSyncing}
            startIcon={<SyncIcon />}
            color="warning"
            variant="outlined"
          >
            {isSyncing ? 'Sincronizzazione...' : 'Reset e Sincronizza'}
          </Button>
          <Button onClick={() => setIsSettingsModalOpen(false)} size="large" variant="outlined">Chiudi</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 8 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
          Sistema integrato TechySumo • Ultimo aggiornamento: Marzo 2026
        </Typography>
      </Box>
    </Container>
  );
}

export default Techyshop;
